<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { ChevronDown, RotateCw } from "@lucide/svelte"
  import { onMount } from "svelte"

  let isStandalone = $state(false)
  let touchStartY = $state(0)
  let touchCurrentY = $state(0)
  let isPulling = $state(false)
  let pullDistance = $state(0)
  let isRefreshing = $state(false)
  let showHint = $state(false)
  let scrollTimeout: ReturnType<typeof setTimeout> | null = $state(null)

  const PULL_THRESHOLD = 80
  const MAX_PULL_DISTANCE = 120

  function isInStandaloneMode(): boolean {
    if (typeof window === "undefined") return false
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true
    )
  }

  function isAtTop(): boolean {
    return window.scrollY === 0
  }

  function handleScroll() {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }

    if (isAtTop() && isStandalone && !isPulling && !isRefreshing) {
      showHint = true
      scrollTimeout = setTimeout(() => {
        showHint = false
      }, 2000)
    } else {
      showHint = false
    }
  }

  function handleTouchStart(e: TouchEvent) {
    if (!isStandalone || !isAtTop() || isRefreshing) return

    touchStartY = e.touches[0].clientY
    isPulling = true
    showHint = false
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
      scrollTimeout = null
    }
  }

  function handleTouchMove(e: TouchEvent) {
    if (!isPulling || !isStandalone || isRefreshing) return

    touchCurrentY = e.touches[0].clientY
    const distance = touchCurrentY - touchStartY

    if (distance > 0 && isAtTop()) {
      e.preventDefault()
      pullDistance = Math.min(distance, MAX_PULL_DISTANCE)
    } else {
      pullDistance = 0
    }
  }

  function handleTouchEnd() {
    if (!isPulling || !isStandalone || isRefreshing) return

    if (pullDistance >= PULL_THRESHOLD) {
      isRefreshing = true
      router.reload()
      setTimeout(() => {
        isRefreshing = false
        pullDistance = 0
      }, 500)
    } else {
      pullDistance = 0
    }

    isPulling = false
    touchStartY = 0
    touchCurrentY = 0
  }

  onMount(() => {
    isStandalone = isInStandaloneMode()

    if (isStandalone) {
      document.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      })
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      })
      document.addEventListener("touchend", handleTouchEnd, { passive: true })
      window.addEventListener("scroll", handleScroll, { passive: true })

      if (isAtTop()) {
        showHint = true
        scrollTimeout = setTimeout(() => {
          showHint = false
        }, 2000)
      }
    }

    return () => {
      document.removeEventListener("touchstart", handleTouchStart)
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
      window.removeEventListener("scroll", handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  })
</script>

{#if isStandalone && showHint && !isPulling && !isRefreshing && pullDistance === 0}
  <div
    class="fixed top-2 right-0 left-0 z-40 flex items-center justify-center transition-opacity duration-300"
    style="opacity: {showHint ? 1 : 0}"
  >
    <div class="flex flex-col items-center gap-1">
      <ChevronDown
        class="text-muted-foreground h-4 w-4 animate-bounce"
        stroke-width={2.5}
      />
      <span class="text-muted-foreground text-xs">Pull to refresh</span>
    </div>
  </div>
{/if}

{#if isStandalone && (pullDistance > 0 || isRefreshing)}
  <div
    class="bg-background/80 fixed top-0 right-0 left-0 z-50 flex items-center justify-center backdrop-blur-sm transition-opacity"
    style="height: {Math.min(
      pullDistance,
      MAX_PULL_DISTANCE,
    )}px; opacity: {Math.min(pullDistance / PULL_THRESHOLD, 1)}"
  >
    <div class="flex flex-col items-center gap-2">
      {#if isRefreshing}
        <RotateCw
          class="text-primary h-6 w-6 animate-spin"
          stroke-width={2.5}
        />
        <span class="text-muted-foreground text-sm">Refreshing...</span>
      {:else if pullDistance >= PULL_THRESHOLD}
        <RotateCw
          class="text-primary h-6 w-6"
          stroke-width={2.5}
          style="transform: rotate({(pullDistance / MAX_PULL_DISTANCE) *
            360}deg)"
        />
        <span class="text-muted-foreground text-sm">Release to refresh</span>
      {:else}
        <RotateCw
          class="text-muted-foreground h-6 w-6"
          stroke-width={2.5}
          style="transform: rotate({(pullDistance / MAX_PULL_DISTANCE) *
            360}deg)"
        />
        <span class="text-muted-foreground text-sm">Pull to refresh</span>
      {/if}
    </div>
  </div>
{/if}
